// User Profile Service - CRUD operations for user_profiles table

import { supabase } from './supabase';
import type { UserProfile } from '../types/user';

/**
 * Get the current user's profile
 */
export async function getCurrentUserProfile(): Promise<UserProfile | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error) {
    console.error('Failed to fetch user profile:', error);
    return null;
  }

  return data;
}

/**
 * Get a user profile by ID
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Failed to fetch user profile:', error);
    return null;
  }

  return data;
}

/**
 * Update the current user's profile
 */
export async function updateUserProfile(
  updates: Partial<Omit<UserProfile, 'id' | 'created_at' | 'updated_at'>>
): Promise<UserProfile | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('user_profiles')
    .update(updates)
    .eq('id', user.id)
    .select()
    .single();

  if (error) {
    console.error('Failed to update user profile:', error);
    return null;
  }

  return data;
}

/**
 * Update user preferences (merges with existing)
 */
export async function updateUserPreferences(
  preferences: Record<string, any>
): Promise<UserProfile | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  // First get current preferences
  const { data: current } = await supabase
    .from('user_profiles')
    .select('preferences')
    .eq('id', user.id)
    .single();

  const mergedPreferences = {
    ...(current?.preferences || {}),
    ...preferences,
  };

  const { data, error } = await supabase
    .from('user_profiles')
    .update({ preferences: mergedPreferences })
    .eq('id', user.id)
    .select()
    .single();

  if (error) {
    console.error('Failed to update user preferences:', error);
    return null;
  }

  return data;
}

/**
 * Get profiles for all members of a household
 */
export async function getHouseholdMemberProfiles(
  householdId: string
): Promise<UserProfile[]> {
  // Get user IDs in the household
  const { data: members, error: membersError } = await supabase
    .from('household_members')
    .select('user_id')
    .eq('household_id', householdId);

  if (membersError || !members) {
    console.error('Failed to fetch household members:', membersError);
    return [];
  }

  const userIds = members.map(m => m.user_id).filter(Boolean);
  if (userIds.length === 0) return [];

  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .in('id', userIds);

  if (error) {
    console.error('Failed to fetch member profiles:', error);
    return [];
  }

  return data || [];
}
